/**
 * Business Inquiry Management Component
 * 업체 소유자가 받은 문의를 관리하는 컴포넌트
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient, unwrapPayload } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Loader2, MessageCircle, CheckCircle, XCircle, Clock } from "lucide-react";
import { getDisplayName } from "@/utils/displayName";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

interface Inquiry {
  id: number;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  type: "inquiry" | "order" | "reservation";
  message: string;
  product_id: number | null;
  quantity: number | null;
  status: "pending" | "responded" | "completed" | "cancelled";
  created_at: string;
  updated_at: string;
}

interface BusinessInquiryManagementProps {
  businessId: string;
}

export function BusinessInquiryManagement({
  businessId,
}: BusinessInquiryManagementProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [responseMessage, setResponseMessage] = useState("");

  // Fetch inquiries
  const { data: inquiriesData, isLoading } = useQuery<{ items: Inquiry[] }>({
    queryKey: ["/api/businesses", businessId, "inquiries"],
    queryFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const res = await fetch(`/api/businesses/${businessId}/inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch inquiries");
      return unwrapPayload<{ items: Inquiry[] }>(await res.json());
    },
    enabled: !!businessId && !!user,
  });

  const inquiries = inquiriesData?.items || [];

  // Update inquiry status
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      inquiryId,
      status,
      responseMessage,
    }: {
      inquiryId: number;
      status: "responded" | "completed" | "cancelled";
      responseMessage?: string;
    }) => {
      return apiRequest("PUT", `/api/businesses/${businessId}/inquiries/${inquiryId}`, {
        status,
        response_message: responseMessage,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/businesses", businessId, "inquiries"],
      });
      setSelectedInquiry(null);
      setResponseMessage("");
      toast({
        title: "상태가 업데이트되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "업데이트 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: Inquiry["status"]) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline">대기중</Badge>;
      case "responded":
        return <Badge className="bg-blue-600">답변됨</Badge>;
      case "completed":
        return <Badge className="bg-green-600">완료</Badge>;
      case "cancelled":
        return <Badge variant="destructive">취소됨</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeLabel = (type: Inquiry["type"]) => {
    switch (type) {
      case "inquiry":
        return "일반 문의";
      case "order":
        return "주문";
      case "reservation":
        return "예약";
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>받은 문의</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (inquiries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>받은 문의</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            아직 받은 문의가 없습니다
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>받은 문의 ({inquiries.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {inquiries.map((inquiry) => (
          <div key={inquiry.id} className="border rounded-lg p-4 space-y-3">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold">
                    {/* meetings 패턴: nickname_display 우선, 그 다음 display_name */}
                    {getDisplayName({
                      nickname_display: (inquiry as any).customer_nickname,
                      display_name: (inquiry as any).customer_display_name,
                      name: inquiry.customer_name,
                    })}
                  </span>
                  {(inquiry as any).customer_nickname && (
                    <Badge variant="secondary" className="text-xs">
                      {(inquiry as any).customer_nickname}
                    </Badge>
                  )}
                  <Badge variant="outline">{getTypeLabel(inquiry.type)}</Badge>
                  {getStatusBadge(inquiry.status)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(inquiry.created_at), {
                    addSuffix: true,
                    locale: ko,
                  })}
                </p>
              </div>
            </div>

            <Separator />

            {/* Inquiry Details */}
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">문의 내용:</span>
                <p className="mt-1">{inquiry.message}</p>
              </div>
              {inquiry.customer_phone && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">전화번호:</span>
                  <a
                    href={`tel:${inquiry.customer_phone}`}
                    className="hover:underline"
                  >
                    {inquiry.customer_phone}
                  </a>
                </div>
              )}
              {inquiry.customer_email && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">이메일:</span>
                  <a
                    href={`mailto:${inquiry.customer_email}`}
                    className="hover:underline"
                  >
                    {inquiry.customer_email}
                  </a>
                </div>
              )}
              {inquiry.type === "order" && inquiry.product_id && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">상품 ID:</span>
                  <span>{inquiry.product_id}</span>
                </div>
              )}
              {inquiry.quantity && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">수량:</span>
                  <span>{inquiry.quantity}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            {inquiry.status === "pending" && (
              <div className="flex gap-2 pt-2">
                <Dialog
                  open={selectedInquiry?.id === inquiry.id}
                  onOpenChange={(open) => {
                    if (open) {
                      setSelectedInquiry(inquiry);
                    } else {
                      setSelectedInquiry(null);
                      setResponseMessage("");
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button size="sm" variant="default" className="flex-1">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      답변하기
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>문의 답변</DialogTitle>
                      <DialogDescription>
                        고객에게 답변을 보내주세요
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>답변 메시지</Label>
                        <Textarea
                          value={responseMessage}
                          onChange={(e) => setResponseMessage(e.target.value)}
                          rows={5}
                          placeholder="답변 내용을 입력하세요"
                        />
                      </div>
                      <Button
                        className="w-full"
                        onClick={() => {
                          updateStatusMutation.mutate({
                            inquiryId: inquiry.id,
                            status: "responded",
                            responseMessage: responseMessage.trim() || undefined,
                          });
                        }}
                        disabled={updateStatusMutation.isPending}
                      >
                        {updateStatusMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            전송 중...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            답변 보내기
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    updateStatusMutation.mutate({
                      inquiryId: inquiry.id,
                      status: "cancelled",
                    });
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  취소
                </Button>
              </div>
            )}

            {inquiry.status === "responded" && (
              <div className="pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    updateStatusMutation.mutate({
                      inquiryId: inquiry.id,
                      status: "completed",
                    });
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  완료 처리
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
